import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import en from '@/locales/en.json';
import ta from '@/locales/ta.json';
import hi from '@/locales/hi.json';
import kn from '@/locales/kn.json';
import te from '@/locales/te.json';
import ml from '@/locales/ml.json';

// ============================================================================
// CENTRALIZED LOCALIZATION ENGINE
// ============================================================================
// This is the ONLY localization engine in the application.
// All translations are loaded here and all languages registered centrally.
// DO NOT create duplicate i18n instances or loaders elsewhere.
// ============================================================================

// All supported languages - registered centrally
const translations = { en, ta, hi, kn, te, ml };

// Track missing keys to avoid duplicate logs (Set for deduplication)
const loggedMissingKeys = new Set();
const MISSING_KEY_LOG_LIMIT = 100; // Prevent excessive logging

// Language metadata for the switcher
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' }
];

// Language codes for easy reference
export const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

const LanguageContext = createContext();

/**
 * Get a nested value from an object using dot-notation key
 * @param {Object} obj - The object to search
 * @param {string} key - Dot-notation key like 'dashboard.title'
 * @returns {string|undefined} The value or undefined if not found
 */
const getNestedValue = (obj, key) => {
  if (!obj || !key) return undefined;
  const keys = key.split('.');
  let value = obj;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return undefined;
    }
  }
  
  return typeof value === 'string' ? value : undefined;
};

/**
 * Log missing translation key (development aid)
 * @param {string} key - The missing key
 * @param {string} language - The language that's missing the key
 * @param {boolean} fallbackUsed - Whether English fallback was used
 */
const logMissingKey = (key, language, fallbackUsed) => {
  // Only log in development and avoid duplicate logs
  if (process.env.NODE_ENV !== 'production' && loggedMissingKeys.size < MISSING_KEY_LOG_LIMIT) {
    const logKey = `${language}:${key}`;
    if (!loggedMissingKeys.has(logKey)) {
      loggedMissingKeys.add(logKey);
      if (fallbackUsed) {
        console.warn(`[i18n] Missing key "${key}" in "${language}" - using English fallback`);
      } else {
        console.error(`[i18n] Missing key "${key}" in both "${language}" AND English!`);
      }
    }
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    const savedLang = localStorage.getItem('merina_language');
    // Validate saved language is in supported list
    if (savedLang && LANGUAGE_CODES.includes(savedLang)) {
      return savedLang;
    }
    return 'en';
  });

  // Wrapped setLanguage to validate language code
  const setLanguage = useCallback((lang) => {
    if (LANGUAGE_CODES.includes(lang)) {
      setLanguageState(lang);
    } else {
      console.warn(`[i18n] Attempted to set unsupported language: ${lang}`);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('merina_language', language);
    // Set document language attribute for accessibility
    document.documentElement.lang = language;
    
    // Add font support for Indian languages
    if (['ta', 'hi', 'kn', 'te', 'ml'].includes(language)) {
      document.documentElement.style.fontFamily = "'Noto Sans', 'Noto Sans Tamil', 'Noto Sans Devanagari', 'Noto Sans Kannada', 'Noto Sans Telugu', 'Noto Sans Malayalam', sans-serif";
    } else {
      document.documentElement.style.fontFamily = "";
    }
  }, [language]);

  /**
   * Translation function with fallback behavior and missing key logging
   * @param {string} key - Dot-notation translation key like 'dashboard.title'
   * @param {Object} params - Parameters to interpolate like {name: 'John'}
   * @returns {string} Translated string or the key if not found
   */
  const t = useCallback((key, params = {}) => {
    if (!key || typeof key !== 'string') {
      return key || '';
    }

    // Try current language first
    let value = getNestedValue(translations[language], key);
    let usedFallback = false;
    
    // Fallback to English if translation not found
    if (value === undefined && language !== 'en') {
      value = getNestedValue(translations['en'], key);
      if (value !== undefined) {
        usedFallback = true;
        logMissingKey(key, language, true);
      }
    }
    
    // If still not found, log error and return key
    if (value === undefined) {
      logMissingKey(key, language, false);
      return key;
    }
    
    // Replace parameters - supports both {{name}} (i18n standard) and {name} (legacy)
    if (Object.keys(params).length > 0) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        // First replace {{param}} (double braces - i18n standard)
        value = value.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), String(paramValue));
        // Then replace remaining {param} (single braces - backward compatibility)
        value = value.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
      });
    }
    
    return value;
  }, [language]);

  // Get current language metadata
  const getCurrentLanguage = useCallback(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  }, [language]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    getCurrentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    languageCodes: LANGUAGE_CODES,
    isEnglish: language === 'en',
    isTamil: language === 'ta',
    isHindi: language === 'hi',
    isKannada: language === 'kn',
    isTelugu: language === 'te',
    isMalayalam: language === 'ml',
    // Utility: Check if a key exists in current language
    hasKey: (key) => getNestedValue(translations[language], key) !== undefined,
    // Utility: Get all translations for a namespace
    getNamespace: (namespace) => translations[language]?.[namespace] || translations['en']?.[namespace] || {}
  }), [language, setLanguage, t, getCurrentLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
